import React from 'react';
import { useTranslation } from 'react-i18next';
import { format, differenceInDays } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Ticket, CheckCircle, XCircle, Clock, Building2, User, CreditCard, FileText, Info, CalendarX, Printer,
} from 'lucide-react';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import ShareCard from '../../components/ShareCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * BookingDetailsDialog — Modal showing full booking details + print/cancel actions.
 * Used in Dashboard "My Bookings" tab.
 *
 * Extracted from App.js (Fase 2) — pure relocation, no logic changes.
 */
export const BookingDetailsDialog = ({ booking, open, onOpenChange }) => {
  const { t } = useTranslation();

  if (!booking) return null;

  const nights = differenceInDays(new Date(booking.check_out), new Date(booking.check_in));
  const isPast = new Date(booking.check_in) < new Date();
  const isUpcoming = new Date(booking.check_in) >= new Date();
  const canRequestCancellation = isUpcoming && booking.status === 'confirmed';

  const handlePrint = () => {
    const printContent = `
      <html>
        <head>
          <title>Booking Confirmation - ${booking.hotel_name}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 2px solid #1e3a5f; padding-bottom: 20px; margin-bottom: 30px; }
            .logo { font-size: 28px; font-weight: bold; color: #1e3a5f; }
            .slogan { font-size: 14px; color: #666; margin-top: 5px; }
            .title { font-size: 24px; color: #1e3a5f; margin: 20px 0; }
            .reference { background: #f5f5f5; padding: 15px; border-radius: 8px; text-align: center; margin-bottom: 30px; }
            .reference-code { font-size: 24px; font-weight: bold; letter-spacing: 2px; color: #1e3a5f; }
            .section { margin-bottom: 25px; }
            .section-title { font-size: 16px; font-weight: 600; color: #1e3a5f; margin-bottom: 10px; border-bottom: 1px solid #eee; padding-bottom: 5px; }
            .detail-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0; }
            .detail-label { color: #666; }
            .detail-value { font-weight: 500; }
            .total { font-size: 20px; font-weight: bold; color: #059669; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; text-align: center; color: #666; font-size: 12px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">Freestays</div>
            <div class="slogan">Commission-free bookings - Your room is FREE!</div>
          </div>
          <div class="title">Booking Confirmation</div>
          <div class="reference">
            <div style="color: #666; font-size: 12px;">Booking Reference</div>
            <div class="reference-code">${booking.booking_id}</div>
            ${booking.sunhotels_booking_id ? `<div style="margin-top: 8px; font-size: 12px; color: #666;">Sunhotels Ref: ${booking.sunhotels_booking_id}</div>` : ''}
          </div>

          <div class="section">
            <div class="section-title">Hotel Details</div>
            <div class="detail-row"><span class="detail-label">Hotel</span><span class="detail-value">${booking.hotel_name}</span></div>
            <div class="detail-row"><span class="detail-label">Room Type</span><span class="detail-value">${booking.room_type}</span></div>
            <div class="detail-row"><span class="detail-label">Board Type</span><span class="detail-value">${booking.board_type || 'Room Only'}</span></div>
            <div class="detail-row"><span class="detail-label">Check-in</span><span class="detail-value">${format(new Date(booking.check_in), 'EEEE, MMMM d, yyyy')}</span></div>
            <div class="detail-row"><span class="detail-label">Check-out</span><span class="detail-value">${format(new Date(booking.check_out), 'EEEE, MMMM d, yyyy')}</span></div>
            <div class="detail-row"><span class="detail-label">Duration</span><span class="detail-value">${nights} night${nights !== 1 ? 's' : ''}</span></div>
            <div class="detail-row"><span class="detail-label">Guests</span><span class="detail-value">${booking.adults} Adult${booking.adults !== 1 ? 's' : ''}${booking.children > 0 ? `, ${booking.children} Child${booking.children !== 1 ? 'ren' : ''}` : ''}</span></div>
          </div>

          <div class="section">
            <div class="section-title">Guest Details</div>
            <div class="detail-row"><span class="detail-label">Name</span><span class="detail-value">${booking.guest_first_name} ${booking.guest_last_name}</span></div>
            <div class="detail-row"><span class="detail-label">Email</span><span class="detail-value">${booking.guest_email}</span></div>
            <div class="detail-row"><span class="detail-label">Phone</span><span class="detail-value">${booking.guest_phone}</span></div>
          </div>

          <div class="section">
            <div class="section-title">Payment Summary</div>
            <div class="detail-row"><span class="detail-label">Total Paid</span><span class="detail-value total">€${booking.final_price?.toFixed(2)}</span></div>
            ${booking.discount_amount > 0 ? `<div class="detail-row"><span class="detail-label">You Saved</span><span class="detail-value" style="color: #059669;">€${booking.discount_amount.toFixed(2)}</span></div>` : ''}
          </div>

          ${booking.special_requests ? `
          <div class="section">
            <div class="section-title">Special Requests</div>
            <p>${booking.special_requests}</p>
          </div>
          ` : ''}

          <div class="footer">
            <p>Freestays - Waanderweg 50a, Emmen, Netherlands</p>
            <p>info@freestays.eu | www.freestays.eu</p>
            <p>Thank you for booking with Freestays!</p>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleRequestCancellation = async () => {
    try {
      await axios.post(`${API}/bookings/${booking.booking_id}/cancellation-request`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('freestays_token')}` }
      });
      toast.success("Cancellation request submitted. We'll contact you shortly.");
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit cancellation request');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ticket className="w-5 h-5 text-primary" />
            Booking Details
          </DialogTitle>
          <DialogDescription>
            {isPast ? 'Past stay' : 'Upcoming stay'} at {booking.hotel_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Status Banner */}
          {/* Status Bar — explicit per-status styling, dark-mode aware */}
          {(() => {
            const status = booking.status;
            const cfg = (() => {
              switch (status) {
                case 'confirmed':
                  return { wrap: 'bg-primary/10 border border-[#00A9E0]/20 dark:bg-primary/20', icon: <CheckCircle className="w-6 h-6 text-primary" />, label: t('dashboard.confirmed', 'Confirmed'), textCls: 'text-foreground' };
                case 'cancelled':
                case 'cancellation_requested':
                  return { wrap: 'bg-red-50 border border-red-200 dark:bg-red-950/40 dark:border-red-800', icon: <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />, label: status === 'cancellation_requested' ? t('dashboard.cancellationRequested', 'Cancellation Requested') : t('dashboard.cancelled', 'Cancelled'), textCls: 'text-red-900 dark:text-red-100' };
                case 'refunded':
                  return { wrap: 'bg-purple-50 border border-purple-200 dark:bg-purple-950/40 dark:border-purple-800', icon: <XCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />, label: t('dashboard.refunded', 'Refunded'), textCls: 'text-purple-900 dark:text-purple-100' };
                case 'no_show':
                  return { wrap: 'bg-gray-100 border border-gray-300 dark:bg-gray-800/60 dark:border-gray-700', icon: <XCircle className="w-6 h-6 text-gray-600 dark:text-gray-400" />, label: t('dashboard.noShow', 'No Show'), textCls: 'text-gray-900 dark:text-gray-100' };
                case 'completed':
                  return { wrap: 'bg-blue-50 border border-blue-200 dark:bg-blue-950/40 dark:border-blue-800', icon: <CheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />, label: t('dashboard.completed', 'Completed'), textCls: 'text-blue-900 dark:text-blue-100' };
                case 'pending_payment':
                  return { wrap: 'bg-amber-50 border border-amber-200 dark:bg-amber-950/40 dark:border-amber-800', icon: <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />, label: t('dashboard.pendingPayment', 'Pending Payment'), textCls: 'text-amber-900 dark:text-amber-100' };
                case 'confirmation_pending':
                  return { wrap: 'bg-orange-50 border border-orange-200 dark:bg-orange-950/40 dark:border-orange-800', icon: <Clock className="w-6 h-6 text-orange-600 dark:text-orange-400" />, label: t('dashboard.confirmationPending', 'Confirmation Pending'), textCls: 'text-orange-900 dark:text-orange-100' };
                default:
                  return { wrap: 'bg-slate-100 border border-slate-300 dark:bg-slate-800/60 dark:border-slate-700', icon: <Clock className="w-6 h-6 text-slate-600 dark:text-slate-400" />, label: (status || 'unknown').replace(/_/g, ' '), textCls: 'text-foreground capitalize' };
              }
            })();
            return (
              <div className={`p-4 rounded-xl flex items-center justify-between ${cfg.wrap}`}>
                <div className="flex items-center gap-3">
                  {cfg.icon}
                  <div>
                    <p className={`font-semibold ${cfg.textCls}`}>{cfg.label}</p>
                    <p className={`text-sm opacity-80 ${cfg.textCls}`}>Ref: {booking.booking_id}</p>
                  </div>
                </div>
                {isUpcoming && (
                  <Badge className="bg-accent text-accent-foreground">
                    {differenceInDays(new Date(booking.check_in), new Date())} days away
                  </Badge>
                )}
              </div>
            );
          })()}

          {/* Hotel Info */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-muted-foreground" />
              Hotel Information
            </h3>
            <div className="bg-secondary/50 rounded-xl p-4 space-y-2">
              <p className="font-semibold text-lg">{booking.hotel_name}</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Room Type</p>
                  <p className="font-medium">{booking.room_type}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Board Type</p>
                  <p className="font-medium">{t(`meals.${booking.board_type || 'Room Only'}`, booking.board_type || 'Room Only')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Dates & Guests */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-secondary/50 rounded-xl p-4">
              <p className="text-muted-foreground text-sm mb-1">Check-in</p>
              <p className="font-semibold">{format(new Date(booking.check_in), 'EEE, MMM d, yyyy')}</p>
            </div>
            <div className="bg-secondary/50 rounded-xl p-4">
              <p className="text-muted-foreground text-sm mb-1">Check-out</p>
              <p className="font-semibold">{format(new Date(booking.check_out), 'EEE, MMM d, yyyy')}</p>
            </div>
            <div className="bg-secondary/50 rounded-xl p-4">
              <p className="text-muted-foreground text-sm mb-1">Duration</p>
              <p className="font-semibold">{nights} Night{nights !== 1 ? 's' : ''}</p>
            </div>
            <div className="bg-secondary/50 rounded-xl p-4">
              <p className="text-muted-foreground text-sm mb-1">Guests</p>
              <p className="font-semibold">
                {booking.adults} Adult{booking.adults !== 1 ? 's' : ''}
                {booking.children > 0 && `, ${booking.children} Child${booking.children !== 1 ? 'ren' : ''}`}
              </p>
            </div>
          </div>

          {/* Guest Details */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-muted-foreground" />
              Guest Details
            </h3>
            <div className="bg-secondary/50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Name</span>
                <span className="font-medium">{booking.guest_first_name} {booking.guest_last_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email</span>
                <span className="font-medium">{booking.guest_email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Phone</span>
                <span className="font-medium">{booking.guest_phone}</span>
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-muted-foreground" />
              Payment Summary
            </h3>
            <div className="bg-[#00A9E0]/5 rounded-xl p-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{booking.status === 'pending_payment' ? t('dashboard.totalDue', 'Total Due') : 'Total Paid'}</span>
                <span className="font-bold text-xl text-primary">€{booking.final_price?.toFixed(2)}</span>
              </div>
              {booking.discount_amount > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">You Saved</span>
                  <span className="font-semibold text-accent">€{booking.discount_amount.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Special Requests — skip if empty, 'none', 'n/a', '-' */}
          {(() => {
            const sr = String(booking.special_requests || '').trim();
            const isEmpty = !sr || ['none', 'n/a', 'na', '-', '–', '—', 'geen'].includes(sr.toLowerCase());
            if (isEmpty) return null;
            return (
              <div className="space-y-3">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  Special Requests
                </h3>
                <div className="bg-yellow-50 border border-yellow-200 dark:bg-yellow-950/30 dark:border-yellow-800/60 rounded-xl p-4">
                  <p className="text-sm text-yellow-900 dark:text-yellow-100">{sr}</p>
                </div>
              </div>
            );
          })()}

          {/* Sunhotels Reference */}
          {booking.sunhotels_booking_id && (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Info className="w-4 h-4" />
              Sunhotels Reference: {booking.sunhotels_booking_id}
            </div>
          )}

          {/* Share Your Trip - Phase 8 */}
          <div className="pt-2 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Share your trip with friends</span>
              <ShareCard
                type="booking"
                data={{
                  hotel: booking.hotel_name || 'Hotel',
                  destination: booking.destination || ''
                }}
                compact
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          {canRequestCancellation && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                  <CalendarX className="w-4 h-4 mr-2" />
                  Request Cancellation
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Request Cancellation?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to request cancellation for this booking? Our team will review your request and contact you regarding the cancellation policy and any applicable refunds.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep Booking</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRequestCancellation} className="bg-red-600 hover:bg-red-700">
                    Submit Request
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />
            Print / Download
          </Button>
          {booking.voucher_url && (
            <Button variant="outline" onClick={() => window.open(booking.voucher_url, '_blank')}>
              <FileText className="w-4 h-4 mr-2" />
              View Voucher
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDetailsDialog;
